import { useMemo, useState } from "react";
import { useI18n } from "../context/I18nContext";

/**
 * Line of Balance chart.
 * X axis: Time (days from start)
 * Y axis: Location index
 * Each activity becomes a polyline through (start, location) → (finish, location).
 */

function toDate(s) { if (!s) return null; const d = new Date(s); return isNaN(d.getTime()) ? null : d; }

const PALETTE = ["#F97316", "#2563EB", "#16A34A", "#DC2626", "#0EA5E9", "#A855F7", "#EAB308", "#10B981", "#EC4899", "#6366F1"];

export default function LineOfBalance({ data }) {
  const { t } = useI18n();
  const [hoverInfo, setHoverInfo] = useState(null);

  const { activities, locations, bounds } = useMemo(() => {
    const acts = data?.activities || [];
    const locs = data?.locations || [];
    if (!acts.length || !locs.length) return { activities: [], locations: [], bounds: null };
    const allDates = [];
    acts.forEach((a) => a.points.forEach((p) => { const s = toDate(p.start); const f = toDate(p.finish); if (s) allDates.push(s); if (f) allDates.push(f); }));
    if (!allDates.length) return { activities: acts, locations: locs, bounds: null };
    const min = new Date(Math.min(...allDates.map((d) => d.getTime())));
    const max = new Date(Math.max(...allDates.map((d) => d.getTime())));
    const days = Math.max(7, Math.round((max - min) / 86400000) + 1);
    return { activities: acts, locations: locs, bounds: { min, max, days } };
  }, [data]);

  if (!activities.length || !locations.length || !bounds) {
    return <div className="text-sm text-zinc-500 py-12 text-center" data-testid="lob-empty">{t("no_tasks")}</div>;
  }

  const W = 980;
  const H = Math.max(380, locations.length * 36);
  const PAD = { l: 160, t: 30, r: 30, b: 50 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const xFor = (date) => PAD.l + (((date - bounds.min) / 86400000) / Math.max(1, bounds.days - 1)) * innerW;
  const yFor = (loc) => {
    const idx = locations.indexOf(loc);
    if (idx < 0) return PAD.t;
    return PAD.t + (idx / Math.max(1, locations.length - 1)) * innerH;
  };

  // x ticks (weekly)
  const xTicks = [];
  const step = Math.max(7, Math.round(bounds.days / 14));
  for (let i = 0; i <= bounds.days; i += step) {
    const d = new Date(bounds.min); d.setDate(d.getDate() + i);
    xTicks.push({ x: xFor(d), label: d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }) });
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto scrollbar-thin" data-testid="lob-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMinYMin meet" style={{ minWidth: 760 }}>
        {/* Grid */}
        {locations.map((loc, i) => (
          <g key={loc}>
            <line x1={PAD.l} x2={W - PAD.r} y1={yFor(loc)} y2={yFor(loc)} stroke="#F4F4F5" strokeWidth={1} />
            <text x={PAD.l - 8} y={yFor(loc) + 4} textAnchor="end" fontSize="11" fontFamily="IBM Plex Mono" fill="#52525B">{loc}</text>
          </g>
        ))}
        {xTicks.map((tk) => (
          <g key={tk.label}>
            <line x1={tk.x} x2={tk.x} y1={PAD.t} y2={H - PAD.b} stroke="#F4F4F5" strokeWidth={1} />
            <text x={tk.x} y={H - PAD.b + 14} textAnchor="middle" fontSize="10" fontFamily="IBM Plex Mono" fill="#71717A">{tk.label}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={PAD.l} x2={W - PAD.r} y1={H - PAD.b} y2={H - PAD.b} stroke="#A1A1AA" />
        <line x1={PAD.l} x2={PAD.l} y1={PAD.t} y2={H - PAD.b} stroke="#A1A1AA" />

        {/* Activity polylines */}
        {activities.slice(0, 10).map((a, ai) => {
          const color = PALETTE[ai % PALETTE.length];
          const pts = a.points
            .map((p) => {
              const s = toDate(p.start); const f = toDate(p.finish);
              if (!s || !f) return null;
              return { x1: xFor(s), x2: xFor(f), y: yFor(p.location), p };
            })
            .filter(Boolean)
            .sort((a, b) => a.y - b.y);
          return (
            <g key={a.activity}>
              {pts.map((seg, i) => (
                // `seg.p` (the raw point) doesn't have a stable id of its own.
                // Compose a key from the segment's coordinates so React keeps
                // identity even when the activity is reordered.
                <g key={`${seg.y}-${seg.x1}-${seg.x2}`}>
                  <line x1={seg.x1} x2={seg.x2} y1={seg.y} y2={seg.y} stroke={color} strokeWidth={3} strokeLinecap="round"
                    onMouseEnter={() => setHoverInfo({ activity: a.activity, ...seg.p })}
                    onMouseLeave={() => setHoverInfo(null)}
                  />
                  {i > 0 && (
                    <line x1={pts[i - 1].x2} x2={seg.x1} y1={pts[i - 1].y} y2={seg.y} stroke={color} strokeWidth={1} strokeDasharray="3,3" />
                  )}
                  <circle cx={seg.x1} cy={seg.y} r={3} fill={color} />
                  <circle cx={seg.x2} cy={seg.y} r={3} fill={color} />
                </g>
              ))}
            </g>
          );
        })}
      </svg>

      <div className="px-4 py-3 border-t border-zinc-100 flex flex-wrap gap-3" data-testid="lob-legend">
        {activities.slice(0, 10).map((a, i) => (
          <div key={a.activity} className="flex items-center gap-1.5 text-[11px] text-zinc-700">
            <span className="inline-block w-3 h-1.5 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="truncate max-w-[180px]" title={a.activity}>{a.activity}</span>
          </div>
        ))}
        {hoverInfo && (
          <div className="ml-auto text-[11px] font-mono text-zinc-700">
            {hoverInfo.activity} @ {hoverInfo.location} · {(hoverInfo.start || "").slice(0, 10)} → {(hoverInfo.finish || "").slice(0, 10)}
          </div>
        )}
      </div>
    </div>
  );
}
